import { describeVariantLines } from '@sitzmark/core';
import { useState } from 'react';
import { Modal, Mono } from '../../components/ui.js';
import { LABEL_SIZES } from './label-geometry.js';
import type { LabelItem, LabelSize } from './label-geometry.js';
import { LabelRunFeedback, ScannabilityNotice, useLabelRun } from './labels.js';
import type { useLabelPrinter } from './labels.js';
import { sizeGrids } from './size-grid.js';

/**
 * Labels for a delivery just received, as grids (FB-1113).
 *
 * "When I go to print labels I get a list, can we show grid view to print
 * variants?" After a delivery the run asked only one-per-unit or one-per-item,
 * with no sizes on screen at all. Now each style that came in is its grid —
 * colors down, sizes across, as the style page and the order lay it out — and
 * each box starts at how many came in, so the common case is still one press,
 * and a size that needs fewer (or a bin tag) is changed in its own box.
 */

export interface DeliveryLabelLine {
  readonly sku: string;
  readonly barcode: string | null;
  readonly productName: string;
  readonly colorName: string | null;
  readonly sizeLabel: string | null;
  readonly altSize: string | null;
  readonly received: number;
  readonly retailCents: number;
  readonly cardPriceCents?: number | null;
}

export function DeliveryLabelsDialog({
  lines,
  size,
  showPrice,
  showBothPrices,
  printer,
  onClose,
}: {
  lines: readonly DeliveryLabelLine[];
  size: LabelSize;
  showPrice: boolean;
  showBothPrices: boolean;
  printer: ReturnType<typeof useLabelPrinter>;
  onClose: () => void;
}) {
  const [quantities, setQuantities] = useState<Record<string, number>>(() =>
    Object.fromEntries(lines.map((line) => [line.sku, line.received])),
  );
  const set = (all: (line: DeliveryLabelLine) => number) =>
    setQuantities(Object.fromEntries(lines.map((line) => [line.sku, all(line)])));

  const items: LabelItem[] = lines
    .filter((line) => (quantities[line.sku] ?? 0) > 0)
    .map((line) => ({
      sku: line.sku,
      barcode: line.barcode,
      ...describeVariantLines({
        productName: line.productName,
        colorName: line.colorName,
        sizeLabel: line.sizeLabel,
        altSize: line.altSize,
      }),
      quantity: quantities[line.sku] ?? 0,
      retailCents: line.retailCents,
      cardPriceCents: line.cardPriceCents ?? null,
    }));
  const count = items.reduce((sum, item) => sum + item.quantity, 0);
  const run = useLabelRun(printer, size, onClose);
  const options = { size, showPrice, showBothPrices, perUnit: true };
  const styles = [...new Set(lines.map((line) => line.productName))];

  return (
    <Modal
      open
      wide
      title="Print labels"
      description={`${LABEL_SIZES[size].label} stock, from your store settings.`}
      onClose={onClose}
      {...run.actions(count, () => run.print(items, options, count))}
    >
      <LabelRunFeedback
        run={run}
        count={count}
        onBrowser={() => run.throughBrowser(items, options)}
      />
      <div className="style-labels__presets">
        <span className="text-secondary">Print</span>
        <button type="button" className="chip" onClick={() => set((line) => line.received)}>
          One per unit received
        </button>
        <button type="button" className="chip" onClick={() => set(() => 1)}>
          One per size
        </button>
        <button type="button" className="chip" onClick={() => set(() => 0)}>
          None
        </button>
      </div>

      {styles.map((style) => (
        <section key={style} className="delivery-labels__style" aria-label={style}>
          <h3 className="delivery-labels__name">{style}</h3>
          {sizeGrids(lines.filter((line) => line.productName === style)).map((grid) => (
            <div className="table-scroll style-labels__table" key={grid.altSize ?? 'one'}>
              <table className="table matrix">
                {grid.label !== null ? (
                  <caption className="matrix__caption">{grid.label}</caption>
                ) : null}
                <thead>
                  <tr>
                    <th />
                    {grid.sizes.map((label, index) => (
                      <th key={label ?? `size-${index}`} className="num">
                        {label ?? 'One size'}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grid.rows.map((row) => (
                    <tr key={row.key}>
                      <th scope="row" className="matrix__row-head">
                        {row.label}
                      </th>
                      {row.cells.map((line, index) =>
                        line === undefined ? (
                          <td key={index} />
                        ) : (
                          <td key={line.sku} className="matrix__cell">
                            <input
                              className="price-input num"
                              inputMode="numeric"
                              aria-label={`Labels for ${line.sku}`}
                              value={String(quantities[line.sku] ?? 0)}
                              onChange={(event) => {
                                const typed = Number.parseInt(event.target.value, 10);
                                setQuantities((current) => ({
                                  ...current,
                                  [line.sku]: Number.isFinite(typed) ? Math.max(typed, 0) : 0,
                                }));
                              }}
                            />
                            <span className="style-labels__onhand">
                              <Mono>{line.sku}</Mono> · {line.received} in
                            </span>
                          </td>
                        ),
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </section>
      ))}

      <ScannabilityNotice items={items} size={size} throughAgent={run.route.kind === 'agent'} />
    </Modal>
  );
}
